import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Dropdown, Drawer, Badge, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BellOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DownOutlined,
  FileTextOutlined,
  InboxOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClientAdmin } from '../context/ClientAdminContext';
import { useNotifications } from '../context/NotificationContext';
import { useAppNav } from '../context/AppNavContext';
import NotificationBell from './NotificationBell';
import TicketButton from './TicketButton';
import DOTheme from './directOrders/doTheme';
import useMediaQuery from '../utils/useMediaQuery';
import { isTicketAdmin } from '../utils/ticketAdmin';
import { useApprovers } from '../utils/approverStore';
import '../responsive.css';
import '../styles/appNav.css';

const COLLAPSE_KEY = 'appNavCollapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const NavItem = ({ item, active, collapsed, onNavigate }) => {
  const content = (
    <>
      <span className="app-nav__icon">{item.icon}</span>
      <span className="app-nav__label">{item.label}</span>
    </>
  );
  const className = `app-nav__item${active ? ' is-active' : ''}`;
  const label = collapsed ? item.label : undefined;

  const element = item.to ? (
    <Link to={item.to} className={className} aria-current={active ? 'page' : undefined} aria-label={label} onClick={onNavigate}>
      {content}
    </Link>
  ) : (
    <button type="button" className={className} aria-current={active ? 'page' : undefined} aria-label={label} onClick={item.onClick}>
      {content}
    </button>
  );

  return collapsed ? (
    <Tooltip title={item.label} placement="right">
      {element}
    </Tooltip>
  ) : (
    element
  );
};

const initialsOf = (user) => {
  const first = user?.firstName || user?.first_name || '';
  const last = user?.lastName || user?.last_name || '';
  const letters = `${first.charAt(0)}${last.charAt(0)}`.trim() || (user?.email || 'U').charAt(0);
  return letters.toUpperCase();
};

const NavigationBar = () => {
  const { user, logout, hasLineVotesAccess } = useAuth();
  useApprovers(Boolean(user));
  const { clientAdmin } = useClientAdmin();
  const { fetchNotifications } = useNotifications();
  const { registerNav } = useAppNav();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [userCollapsed, setUserCollapsed] = useState(readCollapsed);

  const isMobile = useMediaQuery('(max-width: 768px)');
  const isNarrow = useMediaQuery('(max-width: 1180px)');
  const collapsed = !isMobile && (userCollapsed || isNarrow);

  // The sidebar tells the page layout how much room it takes through classes on <html>
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('has-app-nav');
    root.classList.toggle('app-nav-collapsed', collapsed);
    root.classList.toggle('app-nav-mobile', isMobile);
    return () => root.classList.remove('has-app-nav', 'app-nav-collapsed', 'app-nav-mobile');
  }, [collapsed, isMobile]);

  // Hide the floating support button while the sidebar is showing its own
  useLayoutEffect(() => registerNav(), [registerNav]);

  // Fetch notifications when user changes
  useEffect(() => {
    if (user?.email) {
      fetchNotifications(user.email, user.role);
    }
  }, [user, fetchNotifications]);

  const toggleCollapsed = () => {
    const next = !userCollapsed;
    setUserCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch (e) {
      // preference just isn't remembered
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleClientAdminClick = () => {
    // Signed-in client admins go straight to their applications; everyone else to the sign-in page
    navigate(clientAdmin ? '/client-applications' : '/client-admin');
    setMobileMenuVisible(false);
  };

  // Use server-validated roles instead of client-side email checks
  const isEmployeeAdmin = user?.isEmployeeAdmin || false;
  const isDirectOrderAdmin = user?.isDirectOrderAdmin || false;
  const path = location.pathname;

  const primaryItems = [
    { key: 'dashboard', to: '/dashboard', icon: <AppstoreOutlined />, label: 'Dashboard', active: path === '/dashboard' },
    {
      key: 'direct-orders',
      to: '/direct-order',
      icon: <FileTextOutlined />,
      label: 'Direct orders',
      active: path.startsWith('/direct-order') && !path.startsWith('/direct-order/dashboard'),
    },
    { key: 'cfq', to: '/cfq', icon: <SolutionOutlined />, label: 'Call for Quote', active: path.startsWith('/cfq') },
    { key: 'overtime', to: '/overtime', icon: <ClockCircleOutlined />, label: 'Overtime', active: path.startsWith('/overtime') },
    { key: 'inventory', to: '/inventory', icon: <InboxOutlined />, label: 'Inventory', active: path.startsWith('/inventory') },
  ];

  const adminItems = [
    ...(isDirectOrderAdmin
      ? [{ key: 'do-admin', to: '/direct-order/dashboard', icon: <AuditOutlined />, label: 'Direct order admin', active: path.startsWith('/direct-order/dashboard') }]
      : []),
    ...(isEmployeeAdmin
      ? [{ key: 'employees', to: '/employee-admin', icon: <TeamOutlined />, label: 'Employees', active: path.startsWith('/employee-admin') }]
      : []),
    ...(hasLineVotesAccess
      ? [{ key: 'finance', to: '/finance', icon: <BankOutlined />, label: 'Finance', active: path.startsWith('/finance') }]
      : []),
    {
      key: 'client-admin',
      icon: <UserSwitchOutlined />,
      label: 'Client admin',
      onClick: handleClientAdminClick,
      active: path.startsWith('/client-applications') || path.startsWith('/client-admin'),
    },
  ];

  const userMenuItems = [
    { key: 'my-account', label: 'My account', icon: <UserOutlined />, onClick: () => navigate('/my-account') },
    { type: 'divider' },
    { key: 'logout', label: 'Sign out', icon: <LogoutOutlined />, onClick: handleLogout },
  ];

  const displayName = user?.firstName || user?.first_name || 'User';
  const fullName = [displayName, user?.lastName || user?.last_name].filter(Boolean).join(' ');

  const renderBell = (placement, asRow, isCollapsed) => (
    <NotificationBell
      userEmail={user?.email}
      userRole={user?.role}
      placement={placement}
      renderTrigger={({ unreadCount }) => {
        const trigger = (
          <button
            type="button"
            className={asRow ? 'app-nav__item' : 'app-topbar__button'}
            aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          >
            <span className="app-nav__icon">
              <Badge count={unreadCount} size="small" offset={[2, -2]}>
                <BellOutlined />
              </Badge>
            </span>
            {asRow && <span className="app-nav__label">Notifications</span>}
          </button>
        );
        return asRow && isCollapsed ? (
          <Tooltip title="Notifications" placement="right">
            {trigger}
          </Tooltip>
        ) : (
          trigger
        );
      }}
    />
  );

  const renderUser = (isCollapsed) => (
    <Dropdown menu={{ items: userMenuItems }} placement="topLeft" trigger={['click']}>
      <button type="button" className="app-nav__user" aria-label={`Account menu for ${fullName}`}>
        <span className="app-nav__avatar" aria-hidden="true">{initialsOf(user)}</span>
        <span className="app-nav__label app-nav__who">
          <span className="app-nav__name">{fullName}</span>
          <span className="app-nav__email">{user?.email}</span>
        </span>
        {!isCollapsed && <DownOutlined className="app-nav__label app-nav__chevron" />}
      </button>
    </Dropdown>
  );

  const renderNav = (isCollapsed, onNavigate) => (
    <nav className="app-nav__scroll" aria-label="Main">
      <div className="app-nav__list">
        {primaryItems.map((item) => (
          <NavItem key={item.key} item={item} active={item.active} collapsed={isCollapsed} onNavigate={onNavigate} />
        ))}
      </div>
      <div className="app-nav__group">
        <span className="app-nav__group-label">Admin</span>
      </div>
      <div className="app-nav__list">
        {adminItems.map((item) => (
          <NavItem key={item.key} item={item} active={item.active} collapsed={isCollapsed} onNavigate={onNavigate} />
        ))}
      </div>
    </nav>
  );

  const brand = (
    <Link to="/dashboard" className="app-nav__brand" aria-label="Festivals Portal home">
      <span className="app-nav__mark" aria-hidden="true">F</span>
      <span className="app-nav__label app-nav__wordmark">Festivals Portal</span>
    </Link>
  );

  const ticketButton = (isCollapsed) => (
    <TicketButton variant="nav" collapsed={isCollapsed} userEmail={user?.email} isAdmin={isTicketAdmin(user?.email)} />
  );

  return (
    <>
      <DOTheme>
        {isMobile ? (
          <>
            <header className="app-topbar">
              <button type="button" className="app-topbar__button" onClick={() => setMobileMenuVisible(true)} aria-label="Open menu">
                <MenuOutlined />
              </button>
              <Link to="/dashboard" className="app-topbar__title">Festivals Portal</Link>
              <span className="app-topbar__spacer" />
              {renderBell('bottomRight', false, false)}
            </header>
            <Drawer
              open={mobileMenuVisible}
              onClose={() => setMobileMenuVisible(false)}
              placement="left"
              width={288}
              closable={false}
              rootClassName="app-nav-drawer"
              styles={{ body: { padding: 0 } }}
            >
              <div className="app-nav app-nav--drawer">
                <div className="app-nav__top">
                  {brand}
                  <button type="button" className="app-topbar__button" onClick={() => setMobileMenuVisible(false)} aria-label="Close menu">
                    <CloseOutlined />
                  </button>
                </div>
                {renderNav(false, () => setMobileMenuVisible(false))}
                <div className="app-nav__footer">
                  {ticketButton(false)}
                  {renderUser(false)}
                </div>
              </div>
            </Drawer>
          </>
        ) : (
          <aside className="app-nav" aria-label="Sidebar">
            <div className="app-nav__top">{brand}</div>
            {renderNav(collapsed)}
            <div className="app-nav__footer">
              {renderBell('topLeft', true, collapsed)}
              {ticketButton(collapsed)}
              {!isNarrow && (
                <button
                  type="button"
                  className="app-nav__item"
                  onClick={toggleCollapsed}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  <span className="app-nav__icon">{collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}</span>
                  <span className="app-nav__label">Collapse</span>
                </button>
              )}
              {renderUser(collapsed)}
            </div>
          </aside>
        )}
      </DOTheme>
    </>
  );
};

export default NavigationBar;
